/**
 * useCoordinatedAutoSave — debounced autosave that is visible to the approval
 * flush rail. The one sanctioned way for a proposal editor to autosave.
 *
 * Every piece below is a silent-data-loss (or stranded-guard) bug when missed,
 * which is why editors must not hand-roll it:
 *
 *   1. **Registration.** The approval footers settle every registered editor
 *      before dispatching. An unregistered editor lets an edit race the approve.
 *   2. **Freeze.** Once the proposal leaves `pending` the backend rejects
 *      `update_payload`, so no save may arm after the freeze and no armed timer,
 *      queued write or retry may fire through it. A failure that lands after
 *      the freeze settles to idle — the decision carries the live content.
 *   3. **Hold, don't strand.** While `paused` (e.g. AI processing) or while a
 *      decision holds the editors locked, edits are HELD as pending instead of
 *      armed, and re-armed when the hold lifts. The rail keeps reporting the
 *      truth, and nothing is ever left `pending` with no timer to settle it.
 *   4. **Change detection.** Only a payload that differs from the last one
 *      persisted is written, so a revert cancels the write.
 *   5. **Serialized writes.** Every write for the registration — debounced,
 *      flushed, retried, or an extra write via `enqueueWrite` — runs strictly
 *      one-at-a-time, so a write that mints a new action version is never
 *      raced by a second write to the superseded id.
 *
 * The debounce itself is `useAutoSave`; retries reuse its single timer.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { useLatestRef } from '@/lib/hooks/useLatestRef';

import { useAutoSave } from './useAutoSave';
import {
  type LiveActionContent,
  useAutoSaveCoordinationOptional,
} from './useAutoSaveCoordination';

/** Matches the debounce the other proposal autosave editors use. */
const DEFAULT_DEBOUNCE_MS = 2000;
const DEFAULT_RETRY_BASE_DELAY_MS = 2000;
const DEFAULT_RETRY_MAX_DELAY_MS = 30000;

type CoordinationHandle = ReturnType<
  NonNullable<ReturnType<typeof useAutoSaveCoordinationOptional>>['register']
>;

type CoordinationSaveState = 'idle' | 'pending' | 'saving' | 'error';

export type CoordinatedSaveStatus =
  | 'idle'
  | 'pending'
  | 'saving'
  | 'saved'
  | 'error';

export interface RetryClassification {
  retryable: boolean;
  /** Server-provided delay (e.g. `Retry-After`) overriding the backoff once. */
  retryAfterMs?: number;
}

export interface RetryScheduledInfo {
  /** 1-based attempt number of the retry being scheduled. */
  attempt: number;
  delayMs: number;
  delaySource: 'retry-after-header' | 'exponential-backoff';
  error: Error;
}

export interface CoordinatedRetryPolicy {
  /** Decide whether a failed write is transient and worth retrying. */
  classify: (error: Error) => RetryClassification;
  /** First backoff delay; doubles per consecutive failure. Default: 2000ms. */
  baseDelayMs?: number;
  /** Backoff ceiling. Default: 30000ms. */
  maxDelayMs?: number;
  /** Diagnostic hook fired whenever a retry is armed. */
  onRetryScheduled?: (info: RetryScheduledInfo) => void;
}

export interface UseCoordinatedAutoSaveOptions<T> {
  /**
   * Stable identity for the coordination registry. Must survive an action
   * version rollover (key on the version-tree root, not the action id), and be
   * unique per editor within one proposal.
   */
  registrationId: string;
  /** Persists the value. Rejecting marks the save errored. */
  onSave: (value: T) => Promise<void>;
  /**
   * The value already persisted server-side when the editor mounts. Seeds
   * change detection so reverting to it does not issue a no-op write.
   */
  initialSavedValue?: T;
  /**
   * When false the editor cannot persist at all: `save`/`flush` are no-ops, and
   * a save armed before it went false is dropped (and settles idle) instead of
   * writing. Default: true.
   */
  enabled?: boolean;
  /**
   * Temporarily hold edits without dropping them (e.g. while the AI rewrites
   * the proposal). Held edits stay pending on the rail, are still written by an
   * explicit flush, and re-arm when unpaused. Default: false.
   */
  paused?: boolean;
  /** Debounce delay before an armed save fires. Default: 2000ms. */
  debounceMs?: number;
  /** Opt-in background retry for transient failures. */
  retry?: CoordinatedRetryPolicy;
  /** Change-detection key for a value. Default: `JSON.stringify`. */
  serialize?: (value: T) => string;
  /** Live content for the approve stale-data check (registered with the rail). */
  getContent?: () => LiveActionContent | null;
  /** Force-apply the latest server content (STALE_ACTIONS recovery). */
  onResync?: () => void;
}

export interface UseCoordinatedAutoSaveResult<T> {
  /** Arm a debounced save for `value`. Supersedes any armed save or retry. */
  save: (value: T) => void;
  /**
   * Wait out the write queue and persist the pending value now (held or armed).
   * Registered as the coordination flush function. Rejects when the save fails,
   * so settling the rail can block a decision.
   */
  flush: () => Promise<void>;
  saveStatus: CoordinatedSaveStatus;
  /**
   * True while a changed value is pending (armed or held) or any write is in
   * flight. Gate an "adopt incoming server value" sync effect on this.
   */
  isGuardActive: boolean;
  /**
   * Call-time read of the same condition as `isGuardActive`, for decisions made
   * inside effects and handlers that must not wait for a re-render.
   */
  hasPendingChanges: () => boolean;
  /**
   * Adopt an external (non-local) value as the saved baseline: cancels any armed
   * or held save and settles the rail. Call when a sync effect adopts a server
   * value that did not originate from a local save.
   */
  syncSavedBaseline: (value: T) => void;
  /**
   * Record that `value` was persisted by a write outside `onSave` (see
   * `enqueueWrite`). Settles the rail when the pending value is now saved.
   */
  markSaved: (value: T) => void;
  /**
   * Run another write for this registration on the same serialized queue (e.g.
   * an attachment removal). Rejects when frozen or when `write` rejects.
   */
  enqueueWrite: <R>(write: () => Promise<R>) => Promise<R>;
}

export function useCoordinatedAutoSave<T>({
  registrationId,
  onSave,
  initialSavedValue,
  enabled = true,
  paused = false,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  retry,
  serialize,
  getContent,
  onResync,
}: UseCoordinatedAutoSaveOptions<T>): UseCoordinatedAutoSaveResult<T> {
  const coordination = useAutoSaveCoordinationOptional();

  // Commit-synced latest values. Every reader runs after commit (timers, queued
  // writes, the registered flush, event handlers), never during render.
  const onSaveRef = useLatestRef(onSave);
  const enabledRef = useLatestRef(enabled);
  const pausedRef = useLatestRef(paused);
  const retryRef = useLatestRef(retry);
  const serializeRef = useLatestRef(serialize);
  const getContentRef = useLatestRef(getContent);
  const onResyncRef = useLatestRef(onResync);

  const toKey = useCallback(
    (value: T): string => (serializeRef.current ?? JSON.stringify)(value),
    [serializeRef]
  );

  const handleRef = useRef<CoordinationHandle | null>(null);
  /** Change-detection key of the last write that succeeded. */
  const lastSavedRef = useRef<string | null>(
    initialSavedValue === undefined
      ? null
      : (serialize ?? JSON.stringify)(initialSavedValue)
  );
  /** Newest value handed to `save`, and whether it still needs persisting. */
  const pendingValueRef = useRef<T | null>(null);
  const hasPendingRef = useRef(false);
  /** The pending value is held (not armed) because of a pause or decision lock. */
  const heldRef = useRef(false);
  /** Set by onError so `flush` can reject on a failure useAutoSave swallowed. */
  const lastErrorRef = useRef<Error | null>(null);
  /** Consecutive failed attempts for the current pending value (backoff). */
  const retryAttemptRef = useRef(0);
  /**
   * Tail of the serialized write chain. Never rejects — a barrier only;
   * failures come back through the write's own promise and `lastErrorRef`.
   */
  const inFlightRef = useRef<Promise<void> | null>(null);
  /**
   * Monotonic id stamped on each queued write. An older write's failure must not
   * resurrect the pending flag a newer queued write already owns.
   */
  const writeSeqRef = useRef(0);
  /** Filled after useAutoSave returns; read only from its callbacks. */
  const armSaveRef = useRef<
    ((value: T, options?: { delayMs?: number }) => void) | null
  >(null);

  // Render-facing mirrors of the pending / in-flight refs (never read refs in render).
  const [isPending, setIsPending] = useState(false);
  const [isWriting, setIsWriting] = useState(false);

  const setPending = useCallback((pending: boolean) => {
    hasPendingRef.current = pending;
    setIsPending(pending);
  }, []);

  const setCoordinationState = useCallback((state: CoordinationSaveState) => {
    handleRef.current?.setSaveState(state);
  }, []);

  const isFrozen = useCallback(
    () => coordination?.isAutoSaveFrozen() === true,
    [coordination]
  );

  const isHolding = useCallback(
    () =>
      pausedRef.current === true || coordination?.isDecisionLocked() === true,
    [coordination, pausedRef]
  );

  /** Drop whatever is pending and settle the rail (freeze / disable / revert). */
  const settleIdle = useCallback(() => {
    heldRef.current = false;
    setPending(false);
    setCoordinationState('idle');
  }, [setCoordinationState, setPending]);

  /** Append a write to the serialized chain; returns the write's own promise. */
  const chainWrite = useCallback(<R>(run: () => Promise<R>): Promise<R> => {
    const prior = inFlightRef.current ?? Promise.resolve();
    const done = prior.then(run);
    const barrier = done.then(
      () => undefined,
      () => undefined
    );
    inFlightRef.current = barrier;
    setIsWriting(true);
    void barrier.then(() => {
      if (inFlightRef.current === barrier) {
        inFlightRef.current = null;
        setIsWriting(false);
      }
    });
    return done;
  }, []);

  const persist = useCallback(
    (value: T): Promise<void> => {
      // Cleared up front: this value is now being committed, so a concurrent
      // flush joins the queue rather than starting its own write.
      setPending(false);
      heldRef.current = false;
      const seq = (writeSeqRef.current += 1);

      return chainWrite(async () => {
        // Re-check the freeze at execution time: a write queued before the
        // freeze must not issue update_payload after it.
        if (isFrozen()) {
          settleIdle();
          return;
        }
        setCoordinationState('saving');
        try {
          await onSaveRef.current(value);
          lastSavedRef.current = toKey(value);
        } catch (error) {
          // Keep the value pending so a flush (or retry) re-sends it — but only
          // while this is still the latest queued write and the pending value
          // still differs from what is stored (a revert-in-flight must not be
          // retried as a duplicate write).
          if (
            seq === writeSeqRef.current &&
            pendingValueRef.current !== null &&
            toKey(pendingValueRef.current) !== lastSavedRef.current
          ) {
            setPending(true);
          }
          throw error;
        }
      });
    },
    [
      chainWrite,
      isFrozen,
      onSaveRef,
      setCoordinationState,
      setPending,
      settleIdle,
      toKey,
    ]
  );

  const handleSuccess = useCallback(() => {
    lastErrorRef.current = null;
    retryAttemptRef.current = 0;
    // A newer edit that arrived mid-write stays pending — unless it reverted to
    // exactly what this write just stored.
    const stillPending =
      hasPendingRef.current &&
      pendingValueRef.current !== null &&
      toKey(pendingValueRef.current) !== lastSavedRef.current;
    setPending(stillPending);
    setCoordinationState(stillPending ? 'pending' : 'idle');
  }, [setCoordinationState, setPending, toKey]);

  const handleError = useCallback(
    (error: Error) => {
      // A decision froze autosave while this write was in flight: the decision
      // carries the live content and owns persistence now, so this failure is
      // superseded. Latching 'error' would arm the unsaved-changes guard on a
      // proposal with nothing left to save.
      if (isFrozen()) {
        lastErrorRef.current = null;
        retryAttemptRef.current = 0;
        settleIdle();
        return;
      }
      lastErrorRef.current = error;
      setCoordinationState('error');

      const policy = retryRef.current;
      const value = pendingValueRef.current;
      if (!policy || !hasPendingRef.current || value === null) {
        return;
      }
      const { retryable, retryAfterMs } = policy.classify(error);
      if (!retryable) {
        retryAttemptRef.current = 0;
        return;
      }
      const attempt = retryAttemptRef.current;
      retryAttemptRef.current = attempt + 1;
      const base = policy.baseDelayMs ?? DEFAULT_RETRY_BASE_DELAY_MS;
      const max = policy.maxDelayMs ?? DEFAULT_RETRY_MAX_DELAY_MS;
      // The ceiling caps the exponential backoff only. A server-provided
      // Retry-After overrides the backoff (see `retryAfterMs` doc): honour it
      // verbatim, even above the cap, so we never retry a 429 earlier than the
      // server told us to.
      const delayMs = retryAfterMs ?? Math.min(max, base * 2 ** attempt);
      policy.onRetryScheduled?.({
        attempt: attempt + 1,
        delayMs,
        delaySource:
          retryAfterMs !== undefined
            ? 'retry-after-header'
            : 'exponential-backoff',
        error,
      });
      if (isHolding()) {
        heldRef.current = true;
        return;
      }
      armSaveRef.current?.(value, { delayMs });
    },
    [isFrozen, isHolding, retryRef, setCoordinationState, settleIdle]
  );

  /**
   * Fire-time gate for the debounce and retry timer. Drops the armed save when
   * frozen, disabled, or reverted (settling the rail); holds it when paused or
   * decision-locked (rail stays pending).
   */
  const shouldSave = useCallback(() => {
    if (isFrozen() || !enabledRef.current) {
      settleIdle();
      return false;
    }
    if (isHolding()) {
      heldRef.current = true;
      return false;
    }
    if (
      pendingValueRef.current !== null &&
      toKey(pendingValueRef.current) === lastSavedRef.current
    ) {
      settleIdle();
      return false;
    }
    return true;
  }, [enabledRef, isFrozen, isHolding, settleIdle, toKey]);

  const {
    save: armSave,
    saveNow,
    cancelPendingSave,
    saveStatus: autoSaveStatus,
  } = useAutoSave<T>(persist, {
    debounceMs,
    enabled,
    // save() is called from change handlers, not a mount effect — swallowing
    // the first call would drop the user's first edit.
    skipFirstSave: false,
    shouldSave,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  useEffect(() => {
    armSaveRef.current = armSave;
  }, [armSave]);

  const save = useCallback(
    (value: T) => {
      if (!enabledRef.current || isFrozen()) {
        return;
      }
      pendingValueRef.current = value;
      const isUnchanged = toKey(value) === lastSavedRef.current;
      setPending(!isUnchanged);
      setCoordinationState(isUnchanged ? 'idle' : 'pending');
      // A fresh edit starts a new save sequence.
      retryAttemptRef.current = 0;
      if (isHolding()) {
        cancelPendingSave();
        heldRef.current = !isUnchanged;
        return;
      }
      heldRef.current = false;
      // Re-arm either way: a revert must supersede the armed value.
      armSave(value);
    },
    [
      armSave,
      cancelPendingSave,
      enabledRef,
      isFrozen,
      isHolding,
      setCoordinationState,
      setPending,
      toKey,
    ]
  );

  /** Re-arm an edit held by a pause or decision lock once the hold lifts. */
  const rearmHeld = useCallback(() => {
    if (!heldRef.current || isHolding()) {
      return;
    }
    heldRef.current = false;
    if (isFrozen() || !enabledRef.current) {
      settleIdle();
      return;
    }
    const value = pendingValueRef.current;
    if (hasPendingRef.current && value !== null) {
      armSave(value);
    }
  }, [armSave, enabledRef, isFrozen, isHolding, settleIdle]);

  // Resume held edits when unpaused.
  useEffect(() => {
    if (!paused) {
      rearmHeld();
    }
  }, [paused, rearmHeld]);

  // Resume held edits when a decision releases its lock.
  useEffect(() => {
    if (!coordination) {
      return;
    }
    return coordination.subscribe(({ isDecisionLocked }) => {
      if (!isDecisionLocked) {
        rearmHeld();
      }
    });
  }, [coordination, rearmHeld]);

  // An editor that can no longer persist has nothing pending to report.
  useEffect(() => {
    if (enabled) {
      return;
    }
    cancelPendingSave();
    if (hasPendingRef.current || heldRef.current) {
      settleIdle();
    }
  }, [enabled, cancelPendingSave, settleIdle]);

  const flush = useCallback(async (): Promise<void> => {
    if (!enabledRef.current) {
      return;
    }
    // Drain the whole serialized chain, not just its current tail: a write can
    // be appended while we await an earlier one.
    while (inFlightRef.current) {
      const tail = inFlightRef.current;
      await tail;
      if (inFlightRef.current === tail) {
        break;
      }
    }
    if (hasPendingRef.current) {
      lastErrorRef.current = null;
      heldRef.current = false;
      // saveNow cancels any armed debounce or retry and swallows errors into
      // status; lastErrorRef carries the failure back out.
      await saveNow(pendingValueRef.current as T);
    } else if (
      pendingValueRef.current !== null &&
      toKey(pendingValueRef.current) === lastSavedRef.current
    ) {
      // The newest value is already stored (e.g. reverted while an abandoned
      // write failed) — that failure belongs to a discarded payload.
      lastErrorRef.current = null;
    }
    if (lastErrorRef.current) {
      throw lastErrorRef.current;
    }
  }, [enabledRef, saveNow, toKey]);

  const flushRef = useLatestRef(flush);

  // Register with the coordination provider. Keyed only on the registration
  // identity — the ref indirections keep a handle from carrying stale logic.
  // A fresh handle is re-seeded with any state the previous one reported.
  useEffect(() => {
    if (!coordination) {
      return;
    }
    const handle = coordination.register(registrationId);
    handleRef.current = handle;
    handle.setFlushFn(() => flushRef.current());
    if (getContentRef.current) {
      handle.setContentFn(() => getContentRef.current?.() ?? null);
    }
    if (onResyncRef.current) {
      handle.setResyncFn(() => onResyncRef.current?.());
    }
    if (lastErrorRef.current) {
      handle.setSaveState('error');
    } else if (hasPendingRef.current) {
      handle.setSaveState('pending');
    }
    return () => {
      handle.unregister();
      handleRef.current = null;
    };
  }, [coordination, registrationId, flushRef, getContentRef, onResyncRef]);

  const syncSavedBaseline = useCallback(
    (value: T) => {
      lastSavedRef.current = toKey(value);
      // An armed (or held) pre-adoption edit must not fire through and clobber
      // the adopted value.
      cancelPendingSave();
      pendingValueRef.current = null;
      retryAttemptRef.current = 0;
      lastErrorRef.current = null;
      settleIdle();
    },
    [cancelPendingSave, settleIdle, toKey]
  );

  const markSaved = useCallback(
    (value: T) => {
      lastSavedRef.current = toKey(value);
      const pending = pendingValueRef.current;
      if (pending !== null && toKey(pending) === lastSavedRef.current) {
        cancelPendingSave();
        lastErrorRef.current = null;
        retryAttemptRef.current = 0;
        settleIdle();
      }
    },
    [cancelPendingSave, settleIdle, toKey]
  );

  const enqueueWrite = useCallback(
    <R>(write: () => Promise<R>): Promise<R> =>
      chainWrite(async () => {
        if (isFrozen()) {
          throw new Error('Autosave is frozen');
        }
        setCoordinationState('saving');
        try {
          return await write();
        } finally {
          if (lastErrorRef.current) {
            setCoordinationState('error');
          } else {
            setCoordinationState(hasPendingRef.current ? 'pending' : 'idle');
          }
        }
      }),
    [chainWrite, isFrozen, setCoordinationState]
  );

  const hasPendingChanges = useCallback(
    () => hasPendingRef.current || inFlightRef.current !== null,
    []
  );

  const saveStatus: CoordinatedSaveStatus =
    isPending && (autoSaveStatus === 'idle' || autoSaveStatus === 'saved')
      ? 'pending'
      : autoSaveStatus;

  return {
    save,
    flush,
    saveStatus,
    isGuardActive: isPending || isWriting,
    hasPendingChanges,
    syncSavedBaseline,
    markSaved,
    enqueueWrite,
  };
}
